/**
 * The result of computing a user's label-based access for a given permission.
 *
 * - `{ all: true }` — unrestricted access (no label filter applies).
 * - `{ all: false, labelIds }` — access limited to the specified label IDs.
 */
export type LabelAccess = { all: true } | { all: false; labelIds: Set<number> };

/**
 * Minimal shape of an RBAC role model required by `computeUserLabelAccess`.
 * Typed as overloads so callers get precise return types per key.
 */
interface RoleModel {
  get(key: 'permissions'): string[] | undefined;
  get(key: 'label_metadata'): Record<string, number[]> | undefined;
  get(key: string): unknown;
}

/**
 * Minimal shape of an RBAC role-set model required by `computeUserLabelAccess`.
 */
interface RoleSetModel {
  get(key: 'roles'): number[] | undefined;
  get(key: string): unknown;
}

/**
 * Minimal shape of the `$rbac` store required by `computeUserLabelAccess`.
 * Only the fields accessed by this utility are declared.
 */
export interface RbacStoreForLabelAccess {
  collection: { modelById: Record<string | number, RoleModel | undefined> };
  roleSetCollection: { modelById: Record<string | number, RoleSetModel | undefined> };
}

/**
 * Computes the label-based and general access levels for a user for a given permission.
 *
 * Returns `{ all: true }` if the user has unrestricted access (no label filter applies and the user has general `<resource>::<action>` permission).
 * Returns `{ all: false, labelIds: Set<number> }` if access is limited to specific labels.
 * Returns `{ all: false, labelIds: new Set() }` (empty set, effectively denying access) if the user has no role that grants the given permission.
 *
 * @param roleIds - Direct role IDs assigned to the user
 * @param roleSetIds - Role set IDs assigned to the user
 * @param permissionKey - Permission to check (e.g. 'board::read')
 * @param $rbac - The $rbac store
 */
export function computeUserLabelAccess({
  roleIds,
  roleSetIds,
  permissionKey,
  $rbac
}: {
  roleIds: number[];
  roleSetIds: number[];
  permissionKey: string;
  $rbac: RbacStoreForLabelAccess;
}): LabelAccess {
  // Gather all effective role IDs: direct assignments + those derived from role sets
  const allRoleIds = new Set(roleIds);

  for (const roleSetId of roleSetIds) {
    const roleSet = $rbac.roleSetCollection.modelById[roleSetId];
    const roles = roleSet?.get('roles') ?? [];

    for (const roleId of roles) {
      allRoleIds.add(roleId);
    }
  }

  let hasPermission = false;
  const collectedLabelIds: number[] = [];

  for (const roleId of allRoleIds) {
    const role = $rbac.collection.modelById[roleId];
    const permissions = role?.get('permissions') ?? [];

    if (role && permissions.includes(permissionKey)) {
      hasPermission = true;
      const labelMetadata = role.get('label_metadata') ?? {};

      if (labelMetadata[permissionKey] === undefined) {
        // This role grants unrestricted access — no label filter for this permission
        return { all: true };
      }

      collectedLabelIds.push(...labelMetadata[permissionKey]);
    }
  }

  // No role granted the permission — return an empty label set, which denies access to any resource
  if (!hasPermission) {
    return { all: false, labelIds: new Set() };
  }

  return { all: false, labelIds: new Set(collectedLabelIds) };
}

/**
 * Returns true if the user (with computed access) can access a resource with the given label IDs.
 *
 * Access to an unlabeled resource (empty labelIds) depends on whether the user has unrestricted access (`all: true`).
 *
 * @param userAccess - Result of `computeUserLabelAccess`
 * @param resourceLabelIds - Label IDs on the resource being accessed
 */
export function canAccessResource({
  userAccess,
  resourceLabelIds
}: {
  userAccess: LabelAccess;
  resourceLabelIds: number[];
}): boolean {
  if (resourceLabelIds.length === 0) {
    return userAccess.all;
  }
  if (userAccess.all) {
    return true;
  }
  return resourceLabelIds.some((id) => userAccess.labelIds.has(id));
}
